import {env} from "@project/env";
import {experimental_createQueryPersister} from "@tanstack/react-query-persist-client";

/**
 * Cache buster — controls per-deploy cache invalidation for all persisted queries.
 *
 * ## How to inject in CI (GitHub Actions):
 *   - vite build --mode production
 *   - Set via an .env file or environment variable:
 *       VITE_CACHE_VERSION=${{ github.sha }}
 *
 * ## Alternative: vite.config.ts define block
 *   define: { 'import.meta.env.VITE_CACHE_VERSION': JSON.stringify(process.env.GIT_SHA) }
 *
 * ## When to bump:
 *   Bump on breaking data-shape changes (new schema, renamed fields), NOT on every deploy.
 *   Every bump invalidates ALL persisted queries for ALL users on next page load.
 *
 * Defaults to "1" in local development so queries always persist between reloads.
 */
const CACHE_BUSTER = env.VITE_CACHE_VERSION ?? "1";

/**
 * Minimum gcTime (ms) that qualifies a query for per-query persistence.
 * Matches queryPolicyDefaults.persistent.gcTime (24 h).
 * Only queries at the `persistent` tier or above are written to localStorage;
 * ephemeral and warm queries remain memory-only and are never persisted.
 */
const PERSIST_GC_MIN = 24 * 60 * 60 * 1_000;

/**
 * Creates the web-specific per-query persister backed by localStorage.
 *
 * Uses `experimental_createQueryPersister` (TanStack Query v5 forward approach).
 * Each query stores its own key (`@project/web-{queryHash}`) instead of a single
 * monolithic cache blob. Restoration happens lazily when a query mounts — no global
 * blocking restore gate is needed.
 *
 * The returned object's `persisterFn` is set globally on the QueryClient via
 * `setDefaultOptions({ queries: { persister: persisterFn } })` in App.tsx.
 * The `filters.predicate` ensures only `persistent`-tier queries (gcTime ≥ 24 h)
 * are actually written to storage.
 *
 * Call once at module level (App.tsx) — safe because localStorage is always
 * available in a browser context.
 */
export function createWebPersister() {
  return experimental_createQueryPersister({
    buster: CACHE_BUSTER,
    filters: {
      predicate: (query) => (query.options.gcTime ?? 0) >= PERSIST_GC_MIN,
    },
    maxAge: PERSIST_GC_MIN,
    prefix: "@project/web",
    storage: localStorage,
  });
}
